import { Injectable, signal, computed, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private _isCollapsed = signal(false);
  private _isHovered = signal(false);

  private breakpointObserver = inject(BreakpointObserver);

  // Usar toSignal para convertir el observable a signal
  private _isMobile = toSignal(
    this.breakpointObserver.observe('(max-width: 768px)'),
    { initialValue: { matches: false, breakpoints: {} } }
  );

  private _isTablet = toSignal(
    this.breakpointObserver.observe(
      '(min-width: 769px) and (max-width: 1024px)'
    ),
    { initialValue: { matches: false, breakpoints: {} } }
  );

  get isCollapsed() {
    return this._isCollapsed.asReadonly();
  }

  get isHovered() {
    return this._isHovered.asReadonly();
  }

  isMobile = computed(() => this._isMobile()?.matches ?? false);
  isTablet = computed(() => this._isTablet()?.matches ?? false);
  isMobileOrTablet = computed(() => this.isMobile() || this.isTablet());

  // Computed que determina si debemos mostrar el contenido expandido
  shouldShowExpandedContent = computed(() => {
    // En móvil y tablet, nunca mostrar contenido expandido cuando está colapsado
    if (this.isMobileOrTablet() && this._isCollapsed()) {
      return false;
    }
    return !this._isCollapsed() || this._isHovered();
  });

  // Computed que calcula el ancho del sidenav
  sidenavWidth = computed(() => {
    // En móvil y tablet, retornar 0 para que no empuje el contenido
    if (this.isMobileOrTablet()) {
      return 0;
    }

    // Solo en desktop (modo 'side') el ancho afecta el layout
    if (!this._isCollapsed()) return 280;
    return this._isHovered() ? 280 : 72;
  });

  // Computed que calcula el ancho para el layout del header/footer
  layoutWidth = computed(() => {
    // En móvil y tablet, header y footer deben ocupar todo el ancho (0 de margen)
    if (this.isMobileOrTablet()) {
      return 0;
    }

    // Solo en desktop, usar el mismo cálculo que sidenavWidth
    if (!this._isCollapsed()) return 280;
    return this._isHovered() ? 280 : 72;
  });

  toggleCollapse() {
    this._isCollapsed.update((value) => !value);
  }

  setCollapsed(collapsed: boolean) {
    this._isCollapsed.set(collapsed);
  }

  onSidebarHover() {
    // En móvil y tablet, el hover no debe expandir el sidebar
    if (this._isCollapsed() && !this.isMobileOrTablet()) {
      this._isHovered.set(true);
    }
  }

  onSidebarLeave() {
    this._isHovered.set(false);
  }
}
